import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { privacyAPI } from '../lib/api';

const fmtDateTime = (s) => (s ? new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const Badge = ({ on, label }) => (
  <span style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 999, background: on ? '#ECFDF5' : '#F1F5F9', color: on ? '#059669' : '#94A3B8' }}>
    {label}: {on ? 'Yes' : 'No'}
  </span>
);

export default function ConsentLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [date, setDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await privacyAPI.getConsentLogs(1000);
      setLogs(res.data.logs || []);
    } catch (e) {
      toast.error('Failed to load consent logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return logs.filter((l) => {
      const matchesTerm = !term
        || (l.visitor_id || '').toLowerCase().includes(term)
        || (l.user_email || '').toLowerCase().includes(term)
        || (l.ip || '').toLowerCase().includes(term);
      const matchesDate = !date || (l.created_at || '').slice(0, 10) === date;
      return matchesTerm && matchesDate;
    });
  }, [logs, q, date]);

  const exportCSV = () => {
    if (filtered.length === 0) { toast.error('Nothing to export'); return; }
    const headers = ['created_at', 'visitor_id', 'user_email', 'choice', 'analytics', 'marketing', 'necessary', 'ip', 'user_agent'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = filtered.map((l) => headers.map((h) => escape(l[h])).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `consent-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1.5rem' }} data-testid="consent-log-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.75rem', fontWeight: 700, color: '#0076A3', margin: 0 }}>Consent Log</h1>
          <p style={{ fontSize: '0.9375rem', color: '#718096', marginTop: '0.375rem' }}>Auditable record of cookie-consent choices ({filtered.length} of {logs.length}).</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button data-testid="consent-refresh-btn" onClick={load} variant="outline" style={{ borderRadius: 10 }}>Refresh</Button>
          <Button data-testid="consent-export-csv-btn" onClick={exportCSV} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>Export CSV</Button>
        </div>
      </div>

      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <input
            data-testid="consent-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search visitor ID, email or IP…"
            style={{ flex: '1 1 280px', padding: '0.625rem 0.875rem', borderRadius: 10, border: '1px solid #CBD5E1', fontSize: '0.875rem', outline: 'none' }}
          />
          <input
            data-testid="consent-date-input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ padding: '0.625rem 0.875rem', borderRadius: 10, border: '1px solid #CBD5E1', fontSize: '0.875rem', outline: 'none' }}
          />
          {(q || date) && (
            <Button data-testid="consent-clear-btn" onClick={() => { setQ(''); setDate(''); }} variant="outline" style={{ borderRadius: 10 }}>Clear</Button>
          )}
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#A0AEC0' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: '#718096' }} data-testid="consent-empty">No consent records found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#A0AEC0', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ padding: '0.625rem 0.5rem' }}>When</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Visitor / User</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Choice</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Categories</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>IP</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, i) => (
                  <tr key={`${l.visitor_id}-${l.created_at}-${i}`} style={{ borderTop: '1px solid #F0EBE3' }} data-testid="consent-row">
                    <td style={{ padding: '0.75rem 0.5rem', color: '#4A5568', whiteSpace: 'nowrap' }}>{fmtDateTime(l.created_at)}</td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#0076A3' }}>
                      <div style={{ fontWeight: 600 }}>{l.user_email || '—'}</div>
                      <div style={{ fontSize: '0.6875rem', color: '#94A3B8', fontFamily: 'monospace' }}>{l.visitor_id}</div>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <span style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '0.15rem 0.55rem', borderRadius: 999, background: '#FFE7D9', color: '#0076A3', textTransform: 'capitalize' }}>{l.choice}</span>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                        <Badge on={l.analytics} label="Analytics" />
                        <Badge on={l.marketing} label="Marketing" />
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#64748B', fontFamily: 'monospace', fontSize: '0.75rem' }}>{l.ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
